import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { BRAND } from "@/lib/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/process", label: "Process" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

const PERSONA_LINKS = [
  { to: "/salaried", label: "Salaried Professionals" },
  { to: "/self-employed", label: "Self-Employed / Professionals" },
  { to: "/business-owners", label: "Business Owners / MSME" },
  { to: "/property-investors", label: "Property Investors" },
];

const SPECIALIST_LINKS = [
  { to: "/invoice-financing-bangalore", label: "IT Vendor Invoice Financing", tag: "Bangalore" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-all duration-300 bg-brand-cream/85 backdrop-blur-md ${
        scrolled ? "border-b border-brand-sand shadow-[0_8px_24px_-20px_rgba(17,42,34,0.25)]" : "border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between py-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-forest text-brand-gold font-outfit font-extrabold text-lg shadow-[0_8px_24px_-12px_rgba(17,42,34,0.55)] transition-transform duration-300 group-hover:rotate-3">
            S
          </span>
          <span className="font-outfit font-bold text-[17px] tracking-tight text-brand-forest">
            {BRAND.short}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  isActive
                    ? "text-brand-terracotta"
                    : "text-brand-forest/75 hover:text-brand-forest"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <div className="relative group" data-testid="nav-foryou-wrapper">
            <button
              data-testid="nav-foryou-btn"
              className="px-4 py-2 rounded-full text-sm font-semibold text-brand-forest/75 hover:text-brand-forest inline-flex items-center gap-1 transition-colors"
            >
              For You <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 absolute right-0 top-full pt-2 w-80 z-50">
              <div className="card-soft p-2 bg-white">
                {PERSONA_LINKS.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    data-testid={`nav-foryou-link-${p.to.replace("/", "")}`}
                    className="block rounded-xl px-3 py-2.5 text-sm font-medium text-brand-forest/85 hover:bg-brand-sand/60 hover:text-brand-terracotta transition-colors"
                  >
                    {p.label}
                  </Link>
                ))}
                <div className="mt-2 pt-2 border-t border-brand-sand">
                  <div className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-brand-terracotta">Specialist Programmes</div>
                  {SPECIALIST_LINKS.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      data-testid={`nav-foryou-link-${p.to.replace("/", "")}`}
                      className="flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium text-brand-forest/85 hover:bg-brand-sand/60 hover:text-brand-terracotta transition-colors"
                    >
                      <span>{p.label}</span>
                      <span className="ml-2 rounded-full bg-brand-gold/20 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#9c6a18] px-2 py-0.5">{p.tag}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            data-testid="nav-call-btn"
            href={`tel:${BRAND.phoneE164}`}
            className="btn-ghost"
          >
            <Phone className="h-4 w-4" /> {BRAND.phone}
          </a>
          <Link to="/apply" data-testid="nav-apply-btn" className="btn-primary">
            Apply Now
          </Link>
        </div>

        <button
          data-testid="nav-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className="md:hidden grid h-10 w-10 place-items-center rounded-full border border-brand-sand bg-white text-brand-forest"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-brand-sand bg-brand-cream/95 backdrop-blur-md">
          <div className="container-x py-3 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-base font-semibold ${
                    isActive ? "text-brand-terracotta" : "text-brand-forest"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="mt-2 pt-3 border-t border-brand-sand">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-terracotta py-2">For You</div>
              {PERSONA_LINKS.map((p) => (
                <Link
                  key={p.to}
                  to={p.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-foryou-link-${p.to.replace("/", "")}`}
                  className="block py-2.5 text-base font-medium text-brand-forest/85"
                >
                  {p.label}
                </Link>
              ))}
              <div className="mt-3 pt-3 border-t border-brand-sand/70">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-terracotta py-2">Specialist Programmes</div>
                {SPECIALIST_LINKS.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-foryou-link-${p.to.replace("/", "")}`}
                    className="flex items-center justify-between py-2.5 text-base font-medium text-brand-forest/85"
                  >
                    <span>{p.label}</span>
                    <span className="ml-2 rounded-full bg-brand-gold/20 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#9c6a18] px-2 py-0.5">{p.tag}</span>
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/apply"
              onClick={() => setOpen(false)}
              data-testid="mobile-apply-btn"
              className="btn-primary mt-3"
            >
              Apply Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
